import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
} from '@mui/material';
import { Button } from '../../../../shared/components/button/Button';
import { Loader } from '../../../../shared/components/Loader';
import ClienteController from '../../../../controllers/ClienteController';
import { formatCurrency } from '../../../../shared/utils/helpers/numbers';

interface Props {
  aberto: boolean;
  clienteId: number | null;
  clienteNome?: string;
  aoFechar: () => void;
}

/**
 * Extrato da conta corrente do cliente.
 *
 * [25/09/2026] A conta corrente existia no banco desde a fase 2 da devolução - tabela,
 * saldo, lançamento e extrato - e **não tinha tela**. O crédito era gravado certo e
 * ninguém conseguia vê-lo: atendente que recebesse "quanto tenho de crédito?" não tinha
 * onde olhar.
 *
 * Diálogo, e não rota nova: quem procura o saldo de um cliente vai à tela de clientes.
 * Uma rota nova exigiria menu novo e um segundo lugar para procurar o mesmo cliente.
 *
 * O saldo acumulado vem da procedure, linha a linha, e não é somado aqui: a pergunta do
 * operador é "como o saldo chegou nesse número", e uma soma feita na tela seria uma
 * segunda versão da conta, livre para divergir do banco.
 */
export const ModalContaCorrente = ({ aberto, clienteId, clienteNome, aoFechar }: Props) => {
  const [carregando, setCarregando] = useState(false);
  const [movimentos, setMovimentos] = useState<any[]>([]);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    if (!clienteId) return;

    setCarregando(true);
    setErro(null);

    const resposta = await ClienteController.ContaCorrente(clienteId);

    setCarregando(false);

    if (resposta.StatusCode !== 200) {
      setMovimentos([]);
      return setErro('Não foi possível carregar a conta corrente deste cliente.');
    }

    setMovimentos(Array.isArray(resposta.Data) ? resposta.Data : []);
  }, [clienteId]);

  useEffect(() => {
    if (aberto) carregar();
  }, [aberto, carregar]);

  // O saldo é o acumulado da ÚLTIMA linha - a procedure já o calcula em ordem. Somar aqui
  // daria o mesmo número por outro caminho, e dois caminhos divergem no dia em que a regra
  // mudar (um crédito com validade, por exemplo).
  const saldo = movimentos.length > 0
    ? Number(movimentos[movimentos.length - 1]?.saldo_acumulado ?? 0)
    : 0;

  return (
    <Dialog open={aberto} onClose={aoFechar} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: '#D91E18', color: 'common.white' }}>
        Conta corrente {clienteNome ? `— ${clienteNome}` : ''}
      </DialogTitle>

      <DialogContent>
        <Loader loading={carregando}>
          <Box mt={2} mb={2}>
            <Typography variant="subtitle2" color="text.secondary">
              Saldo atual
            </Typography>
            {/* Positivo = a empresa deve ao cliente. O sinal é o que importa aqui, então
                ele é dito em palavras: "R$ 300,00" sozinho não diz de quem é o dinheiro. */}
            <Typography variant="h6">
              {formatCurrency(saldo)}{' '}
              <Typography component="span" variant="body2" color="text.secondary">
                {saldo > 0.004
                  ? 'a favor do cliente'
                  : saldo < -0.004
                    ? 'devido pelo cliente'
                    : 'sem saldo'}
              </Typography>
            </Typography>
          </Box>

          {erro && (
            <Typography variant="body2" color="error" mb={2}>
              {erro}
            </Typography>
          )}

          {!erro && movimentos.length === 0 && !carregando && (
            /* Extrato vazio não é falha: a maioria dos clientes nunca teve crédito.
               Dizer isso evita a dúvida de "não carregou" ou "não tem". */
            <Typography variant="body2" color="text.secondary">
              Este cliente não tem movimento em conta corrente.
            </Typography>
          )}

          {movimentos.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Origem</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell align="right">Saldo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movimentos.map((m: any) => (
                  <TableRow key={m.ccc_id}>
                    <TableCell>
                      {m.ccc_data ? String(m.ccc_data).substring(0, 10).split('-').reverse().join('/') : ''}
                    </TableCell>
                    <TableCell>{m.ccc_tipo}</TableCell>
                    <TableCell>{m.ccc_descricao}</TableCell>
                    <TableCell>
                      {/* O id da origem ao lado do nome: é o que permite conferir a linha
                          contra o documento que a gerou. */}
                      {m.ccc_origem}
                      {m.ccc_idOrigem ? ` ${m.ccc_idOrigem}` : ''}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(Number(m.ccc_valor ?? 0))}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(Number(m.saldo_acumulado ?? 0))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Loader>
      </DialogContent>

      <DialogActions>
        <Button title="Fechar" variant="secondary" onClick={aoFechar} />
      </DialogActions>
    </Dialog>
  );
};

export default ModalContaCorrente;
